<template>
  <v-dialog
    v-model="dialog"
    max-width="800"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    transition="dialog-bottom-transition"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn v-if="!bigButton" @click="initialize" :loading="isLoading" v-bind="attrs" v-on="on" icon>
        <v-icon color="grey lighten-1">mdi-upload</v-icon>
      </v-btn>
      <div v-else>
        <v-btn
          @click="initialize"
          :loading="isLoading"
          v-bind="attrs"
          v-on="on"
          dense
          small
          flat
          depressed
          color="warning"
          >{{ $t(btnTextAlt) }}</v-btn
        >
      </div>
    </template>
    <v-card v-if="!isLoading">
      <v-card-title>
        <span>{{ $t(title) }}</span>
        <v-spacer />
        <v-btn icon>
          <v-icon @click="close" color="grey lighten-1">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        {{ $t(text) }}
      </v-card-text>
      <div class="mx-6 my-6">
        <file-upload-multi
          :instruction="instruction"
          :snackbarTitle="multiSteps.frontLeft.text"
          :example="multiSteps.frontLeft.example"
          :overlay="multiSteps.frontLeft.overlay"
          :text="multiSteps.frontLeft.text"
          :textAlt="multiSteps.frontLeft.textAlt"
          :filesCb="multiSteps.frontLeft.filesCb"
          :displayImages="getRegistrationImageFront"
          :handleDeleteCb="multiSteps.frontLeft.handleDelete"
          :handleUploadCb="multiSteps.frontLeft.handleUpload"
          data-test-registration-front
        ></file-upload-multi>
        <file-upload-multi
          :instruction="instruction"
          :snackbarTitle="multiSteps.rearLeft.text"
          :example="multiSteps.rearLeft.example"
          :overlay="multiSteps.rearLeft.overlay"
          :text="multiSteps.rearLeft.text"
          :textAlt="multiSteps.rearLeft.textAlt"
          :filesCb="multiSteps.rearLeft.filesCb"
          :displayImages="getRegistrationImageBack"
          :handleDeleteCb="multiSteps.rearLeft.handleDelete"
          :handleUploadCb="multiSteps.rearLeft.handleUpload"
          data-test-registration-back
        ></file-upload-multi>
      </div>
      <v-card-actions>
        <v-btn class="mx-4 mb-4" :disabled="!valid" @click="update">
          {{ $t(btnText) }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-card>
      <v-skeleton-loader v-if="isLoading" type="article, article, actions"></v-skeleton-loader>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import FileUploadMulti from "@/components/files/FileUploadMulti.vue";
import ThgQuoteWorkitemRegistrationCard from "@/components/thg/ThgQuoteWorkitemRegistrationCard.vue";
import { AssetEnum } from "@/lib/AssetRepository";
import { IReportInstructionData } from "@/lib/interfaces/Report/IReportInstructionData";
import { requiredRule } from "@/lib/rules/requiredRule";
import DarkModeHighlightMixin from "@/mixins/DarkModeHighlightMixin.vue";
import { ThgCreateModule } from "@/store/modules/thg.create.store";
import { ThgPortalModule } from "@/store/modules/thg.portal.store";
import { Component } from "vue-property-decorator";
import { ErrorLogModule } from "@/store/modules/error-log";

@Component({
  components: {
    ThgQuoteWorkitemRegistrationCard,
    FileUploadMulti
  }
})
export default class ThgQuoteUpdateRegistrationImageDialog extends DarkModeHighlightMixin {
  bigButton = false;
  isLoading = false;
  dialog = false;

  title = "components.thg.ThgUpdateRegistrationImagesDialog.title";
  btnText = "components.thg.ThgUpdateRegistrationImagesDialog.btnText";
  btnTextAlt = "components.thg.ThgUpdateRegistrationImagesDialog.title";
  text = "components.thg.ThgUpdateRegistrationImagesDialog.text";

  multiSteps = {
    frontLeft: {
      example: AssetEnum.registration,
      overlay: AssetEnum.registrationSchema,
      text: "report.thgRegistration.registrationFront",
      textAlt: "report.thgRegistration.registrationFrontAlt",
      filesCb: ThgCreateModule.registrationImageFront,
      handleUpload: ThgCreateModule.updateRegistrationFront,
      handleDelete: ThgCreateModule.removeRegistrationFront
    },
    rearLeft: {
      example: AssetEnum.registrationBack,
      overlay: AssetEnum.registrationBackOutline,
      text: "report.thgRegistration.registrationBack",
      textAlt: "report.thgRegistration.registrationBackAlt",
      filesCb: ThgCreateModule.registrationImageBack,
      handleUpload: ThgCreateModule.updateRegistrationBack,
      handleDelete: ThgCreateModule.removeFromRegistrationImageBack
    }
  };

  instruction: IReportInstructionData = {
    title: "report.thgRegistration.title",
    text: "report.thgRegistration.instructionText",
    pictures: [AssetEnum.registrationCorrectInstructions, AssetEnum.registrationIncorrectInstructions],
    example: AssetEnum.registrationExample,
    display: true
  };

  get valid() {
    return ThgCreateModule.registrationImageFront.length === 1 && ThgCreateModule.registrationImageBack.length === 1;
  }

  get getRegistrationImageFront() {
    return ThgCreateModule.registrationImageFront;
  }

  get getRegistrationImageBack() {
    return ThgCreateModule.registrationImageBack;
  }

  get loading() {
    return this.isLoading;
  }

  async update() {
    this.isLoading = true;
    try {
      await ThgPortalModule.updateRegistrationImages();
    } catch (error) {
      this.$toast.error(this.$t("components.thg.ThgUpdateRegistrationImagesDialog.error").toString());
      ErrorLogModule.addErrorLog({
        name: "Update registrationimage error",
        message: "Could not update registration image."
      });
    } finally {
      this.isLoading = false;
      this.dialog = false;
    }
  }

  initialize() {
    this.isLoading = true;
    this.isLoading = false;
  }

  close() {
    this.dialog = false;
  }

  /**
   * Rules
   */
  get requiredRules() {
    const rules = [];

    rules.push(requiredRule());

    return rules;
  }
}
</script>
